import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Employee } from '@shared/classes/employee.class';
import { ProductCategory } from '@shared/classes/product-category.class';
import { ProductItemGroup } from '@shared/classes/product-item-group.class';
import { ProductItem } from '@shared/classes/product-item.class';
import { ProductList } from '@shared/classes/product-list.class';
import { ProductVariant } from '@shared/classes/product-variant.class';
import { Product } from '@shared/classes/product.class';
import { Store } from '@shared/classes/store.class';
import { PRODUCT_VARIANT_LIST_TYPE } from '@shared/constants';
import { PRODUCT_TYPES } from '@shared/constants/product-types';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { IInventoryEvent } from '@shared/interfaces/inventory-event.interface';
import { ProductVariantProvider } from '@core/services/resources/product-variant-db-entity.provider';
import { AppointmentModalService } from '@core/services/system/appointment-modal/appointment-modal.service';
import { AppointmentService } from '@core/services/system/appointment/appointment.service';
import { CartService } from '@core/services/system/cart/cart.service';
import { CollectionViewService } from '@core/services/system/collection-view.service';
import { InvoicesService } from '@core/services/system/invoices.service';
import { LogService } from '@core/services/system/logger/log.service';
import { ModalService } from '@core/services/system/modal.service';
import { PlatformService } from '@core/services/system/platform/platform.service';
import { SecurityService } from '@core/services/system/security.service';
import { TableEnforceService } from '@core/services/system/table-select-enforcement.service';
import { TippingService } from '@core/services/system/tipping/tipping.service';
import { UpdatesService } from '@core/services/system/updates.service';
import { VirtualScrollerComponent } from '@core/capacitor-plugins/capacitor-plugins.class';
import { ProductDetailsModal } from '@features/collection-view/product-details-modal/product-details-modal.component';
import { from, innerJoin, notEquals, property, Query, query, oneOf, by } from '@paymash/capacitor-database-plugin';
import { firstValueFrom, from as rxFrom, merge, Observable, of, tap, Subject, Subscription } from 'rxjs';
import { concatMap, delay, map, takeWhile, switchMap, catchError } from 'rxjs/operators';
import { DbDaoService } from '@core/services/db/db-dao.service';
import { DbResponse } from '@core/services/db/db-dao.utils';
import { ignoreImageProps } from '@core/services/system/logger';
import { PosSettingsService } from '@core/services/system/pos-settings.service';
import { CartInteractionCoordinator } from '@core/services/system/cart/cart-interaction.contracts';

@Component({
  selector: 'pos-virtual-product-list',
  templateUrl: './virtual-product-list.component.html',
  styleUrls: ['./virtual-product-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualProductListComponent implements OnInit, AfterViewInit {
  @Input() parentScroll: Element;
  @Input() types: string[] = [];
  @ViewChild('virtualScroll') virtualScroll: VirtualScrollerComponent;
  public cellsQuantity: number;
  public cellWidth: number;
  public readonly productItemList = signal<ProductItemGroup[]>([]);
  public isCartNotEmpty: boolean = false;
  public isMobile: boolean = false;
  // TODO need refactoring to signal: ProductList is mutable and virtual-scroller still owns imperative viewport recalculation.
  private productList = new ProductList();
  private requiredUpdatesTypes = [
    UPDATES_TYPES.Product,
    UPDATES_TYPES.ProductCategory
  ];
  private activeStore: Store = null;
  private numbersOfProductsInRow: string;
  private readonly logger = this.logService.createLogger('VirtualProductListComponent');

  // Performance optimizations
  private updateBatchTimer: any;
  private categoryLoadSubscriptions = new Map<string, Subscription>();

  // Click request cancellation mechanism - switchMap automatically cancels previous unfinished requests
  private productClickSubject$ = new Subject<{ productItem: ProductItem; eventTarget: any }>();

  get isServiceList() {
    return this.types.some((type) => type === PRODUCT_TYPES.SERVICE);
  }

  constructor(
    private collectionViewService: CollectionViewService,
    private modalService: ModalService,
    private dbDaoService: DbDaoService,
    private productVariantProvider: ProductVariantProvider,
    private updatesService: UpdatesService,
    private cartService: CartService,
    private tableEnforceService: TableEnforceService,
    private invoicesService: InvoicesService,
    private cdr: ChangeDetectorRef,
    private tippingService: TippingService,
    private renderer: Renderer2,
    private securityService: SecurityService,
    private appointmentModalService: AppointmentModalService,
    private appointmentService: AppointmentService,
    private platformService: PlatformService,
    private destroyRef: DestroyRef,
    private logService: LogService,
    private posSettingsService: PosSettingsService,
    private cartInteractionCoordinator: CartInteractionCoordinator,
  ) {
    this.initCartNotEmptyEffect();
  }

  private initCartNotEmptyEffect(): void {
    let prevCartNotEmpty: boolean | undefined;
    effect(() => {
      const invoice = this.cartService.activeInvoiceUpdated();
      const value = Boolean(invoice?.totalProductsCount);
      if (value !== prevCartNotEmpty) {
        prevCartNotEmpty = value;
        this.isCartNotEmpty = (value && this.isMobile);
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.numbersOfProductsInRow = this.posSettingsService.getNumberOfProductsInRowOnCollectionView();
    this.isMobile = this.platformService.isMobile;
    this.activeStore = this.securityService.getActiveStore();
    this.setQuantity(this.cellsQuantity);

    const streams$ = [];

    for (let i = 0; i < this.requiredUpdatesTypes.length; i++) {
      const currentUpdateType = this.requiredUpdatesTypes[i];
      this['list' + currentUpdateType['type']] = [];
      streams$.push(this.updatesService.updatesEmitters[currentUpdateType['type']].pipe(
        tap((data: any) => {
          if (data) {
            this.handleUpdateData(data, currentUpdateType['type']);
          }
        })
      ));
    }

    // Setup product click stream with automatic cancellation using switchMap
    // switchMap automatically cancels previous unfinished request when new click arrives
    this.productClickSubject$.pipe(
      switchMap(({ productItem, eventTarget }) =>
        rxFrom(this.cartInteractionCoordinator.track(this.handleProductClickProcess(productItem, eventTarget))).pipe(
          catchError((error) => {
            this.logger.error(error, 'productClick', undefined, { productItemUuid: productItem?.uuid });
            return of(null);
          })
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.cdr.markForCheck();
    });

    merge(...streams$).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  ngAfterViewInit(): void {
    this.updateCellParams();
    this.getProductsAndCategoriesFromDb();
  }

  ngOnDestroy(): void {
    // Persist any coalesced tap-adds before leaving the screen (no-op if nothing pending).
    this.cartService.flushSave();
    if (this.updateBatchTimer) {
      clearTimeout(this.updateBatchTimer);
    }
    // Cancel all pending category loads
    this.categoryLoadSubscriptions.forEach((sub) => sub.unsubscribe());
    this.categoryLoadSubscriptions.clear();

    this.collectionViewService.setProducVariantModalData(null);

    // Complete the subject to prevent memory leaks
    this.productClickSubject$.complete();

    // Boundary: drop any tracked add and release pending gates when the product list is torn down.
    this.cartInteractionCoordinator.reset();
  }

  private setQuantity(cellsQuantity: number) {
    this.cellsQuantity = cellsQuantity;
  }

  private getProductsAndCategoriesFromDb(categoryUuid?: string) {
    const key = categoryUuid || 'root';

    // Cancel duplicate/stale loading for this category
    if (this.categoryLoadSubscriptions.has(key)) {
      this.categoryLoadSubscriptions.get(key).unsubscribe();
    }

    const subscription = merge(
      this.getAllCategoriesFromDb(categoryUuid).pipe(
        map((categoriesListData) => ({ categoriesListData: categoriesListData.data, productListData: undefined }))
      ),
      this.getAllProductsFormDb(categoryUuid).pipe(
        concatMap((productListData, idx) => (idx === 0 ? of(productListData) : of(productListData).pipe(delay(500)))),
        map((productListData) => ({ productListData, categoriesListData: undefined })),
        takeWhile(() => {
          return this.productList.hasOpenCategory(categoryUuid);
        })
      )
    ).subscribe({
      next: ({ categoriesListData, productListData }) => {
        // Double check if category is still open before adding data
        // This prevents "zombie" data if category was closed while DB fetch was in progress
        if (categoryUuid && !this.productList.hasOpenCategory(categoryUuid)) {
          return;
        }

        if (categoriesListData) {
          this.addCategoriesToList(categoriesListData, categoryUuid);
        }
        if (productListData) {
          this.addProductsToList(productListData, categoryUuid);
        }

        // Use batching for deferred update
        this.scheduleUpdateProductItemList();
      },
      error: (error) => {
        this.logger.error(error, 'getProductsAndCategoriesFromDb');
      }
    });

    this.categoryLoadSubscriptions.set(key, subscription);
  }

  private getAllCategoriesFromDb(categoryUuid: string): Observable<DbResponse> {
    if (!categoryUuid || (categoryUuid && categoryUuid === 'root')) {
      categoryUuid = null;
    }
    const queryParams: Query = { visible: true, parentCategoryUuid: categoryUuid, deleted: false };
    return rxFrom(this.dbDaoService.getAllData(UPDATES_TYPES.ProductCategory.type, queryParams));
  }

  private getAllProductsFormDb(productCategoryUuid?: string): Observable<any> {
    productCategoryUuid = productCategoryUuid || 'root';

    const activeStore = this.securityService.getActiveStore();

    const queryParams = query({
      'p.visible': true,
      'p.deleted': false,
      'p.ext_type': null,
      'ext.ext_type': 'category_store',
      'ext.categoryUuid': productCategoryUuid,
    });

    if (activeStore) {
      queryParams.push({
        'ext.storeUuid': activeStore.uuid,
      });
    }

    if (this.tippingService.isTipping && this.tippingService.productUuid) {
      queryParams.push({ 'p.uuid': notEquals(this.tippingService.productUuid) });
    }
    if (this.types.length) {
      queryParams.push({ 'p.type': oneOf(...this.types,  PRODUCT_TYPES.DEPOSIT) });
    }

    return this.dbDaoService.getAllDataChunked(from(UPDATES_TYPES.Product.type, 'p'), queryParams, {
      join: [innerJoin(UPDATES_TYPES.Product.type, 'ext', { 'ext.productUuid': property('p.uuid') })],
      chunkSize: 15,
      order: [by('p.fieldToSort')],
      select: ['p.uuid', 'p.visible', 'p.isNew', 'p.isSale', 'p.bgColor', 'p.name', 'p.images', 'p.wasPrice', 'p.price'],
    });
  }

  private addProductsToList(products: Product[], categoryUuid?: string) {
    const productList = this.sortData(products);
    this.productList.addProducts(productList, categoryUuid);
    this.notifyAboutProductDublicates(productList, categoryUuid);
  }

  private addCategoriesToList(productCategories: ProductCategory[], categoryUuid?: string) {
    productCategories = this.sortData(productCategories, true);
    this.productList.addProductCategories(productCategories, categoryUuid);
  }

  private sortData(data: any[], isCategory: boolean = false) {
    return [...data].sort((a, b) => {
      const nameA = a.name?.toLowerCase();
      const nameB = b.name?.toLowerCase();
      const nameOrder = nameA > nameB ? 1 : nameB > nameA ? -1 : 0;

      if (isCategory) {
        const positionOrder = a?.positionInParentProductCategory - b?.positionInParentProductCategory;
        return positionOrder || nameOrder;
      }
      return nameOrder;
    });
  }

  handleTouchStart(event: IInventoryEvent) {
    const { uuid } = event.dataset;
    const productItem = this.findProductItem(uuid);
    if (productItem && !productItem.isCategory) {
      this.productLongClick(productItem);
    }
  }

  handleTouchEnd(event: IInventoryEvent) {
    const productItem: ProductItem = this.findProductItem(event?.dataset?.uuid);
    if (!productItem) {
      return;
    }
    if (!productItem.isCategory) {
      this.productClick(productItem, event.eventTarget);
      return;
    }

    const expanded = !productItem.expanded;
    const { uuid: categoryUuid } = productItem;

    // Set expanded immediately for instant visual feedback
    productItem.expanded = expanded;
    this.cdr.markForCheck();

    if (expanded) {
      this.productList.closeCategory(categoryUuid);
      productItem.expanded = true;
      this.productList.openCategory(categoryUuid);
      this.getProductsAndCategoriesFromDb(categoryUuid);
    } else {
      this.productList.hideProductsAndCategory(categoryUuid);
      this.updateProductItemList();
    }
  }

  private findProductItem(uuid: string): ProductItem | null {
    if (!this.virtualScroll?.viewPortItems) {
      return null;
    }

    const { viewPortItems } = this.virtualScroll;
    const length = viewPortItems.length;

    // Optimization: use regular for loop instead of for-of
    for (let i = 0; i < length; i++) {
      const productItems: ProductItemGroup = viewPortItems[i];
      if (!productItems?.productItemList) continue;

      const productItem = productItems.productItemList.find((p) => p.uuid === uuid);
      if (productItem) {
        return productItem;
      }
    }
    return null;
  }

  private async findProductFromDatabase(uuid: string): Promise<Product> {
    return this.dbDaoService.getDataByUUID(UPDATES_TYPES.Product.type, uuid).then(({ data }): Product => new Product(data));
  }

  private updateProductItemList(): void {
    this.productItemList.set(this.productList.getProductItemGroups(this.cellsQuantity));
  }

  // Batch updates to prevent multiple recalculations
  private scheduleUpdateProductItemList(): void {
    if (this.updateBatchTimer) {
      return; // Update already scheduled
    }

    // Defer update to next microtask
    this.updateBatchTimer = setTimeout(() => {
      this.updateBatchTimer = null;
      this.updateProductItemList();
    }, 0);
  }

  private productClick(productItem: ProductItem, eventTarget: any) {
    // Just emit the click event - switchMap will handle cancellation automatically
    this.productClickSubject$.next({ productItem, eventTarget });
  }

  private async handleProductClickProcess(productItem: ProductItem, eventTarget: any): Promise<void> {
    if (this.isDisabledSelectProduct()) {
      return;
    }

    this.addRippleEffect(eventTarget);
    const { productCategoryUuid } = productItem;
    const product: Product = await this.findProductFromDatabase(productItem?.data?.uuid);

    if (!product?.hasCustomOptions && !product?.forcePriceInputOnSale) {
      if (this.isServiceList) {
        const defaultEmployee = this.appointmentService.getDefaultEmployee();
        if (defaultEmployee) {
          this.addServiceToCard(product, defaultEmployee);
          return;
        }
        this.openProductVariantModal(product, productCategoryUuid, eventTarget);
        return;
      }
      try {
        const variant: ProductVariant = await firstValueFrom(this.productVariantProvider.getByUuid(product?.variants?.[0]?.uuid));
        await this.addProductToCart(variant, product, productCategoryUuid, '');
      } catch (error) {
        this.logger.error(error, 'productClick:DbDaoService:getByUuid:', undefined, { product: ignoreImageProps(product) });
      }
    } else {
      if (product.variants.length > 1 || product?.forcePriceInputOnSale || this.isServiceList) {
        this.openProductVariantModal(product, productCategoryUuid, eventTarget);
        return;
      }
      try {
        const variant: ProductVariant = await firstValueFrom(this.productVariantProvider.getByUuid(product?.variants?.[0]?.uuid));
        const shortName: string = variant.getVariantConcatenatedName();
        await this.addProductToCart(variant, product, productCategoryUuid, shortName);
      } catch (error) {
        this.logger.error(error, 'productClick:productVariantProvider:getByUuid witn many variants', undefined, {
          product: ignoreImageProps(product),
        });
        this.openProductVariantModal(product, productCategoryUuid, eventTarget);
      }
    }
    this.cdr.markForCheck();
  }

  private isDisabledSelectProduct() {
    return (
      !this.isServiceList &&
      (this.tableEnforceService.checkForTableEnforcementAndShowAlert() || this.cartService.checkPartialPaymentInInvoice())
    );
  }

  private async productLongClick(productItem: ProductItem) {
    if (this.cartService.checkPartialPaymentInInvoice()) {
      return;
    }
    const product = await this.findProductFromDatabase(productItem.uuid);
    if (this.isServiceList) {
      return this.openCustomServiceModal(product);
    }
    this.openProductDetailsModal(product, productItem.productCategoryUuid);
  }

  private async openProductDetailsModal(product: Product, productCategoryUuid: string) {
    const productDetailsModal = await this.modalService.presentModal(ProductDetailsModal, {
      product,
      productCategory: productCategoryUuid,
      source: 'collection',
    });
    await productDetailsModal.present();
  }

  private openCustomServiceModal(product: Product) {
    let service = this.appointmentService.makeServiceToAddData(product, product?.price, null);
    service = this.appointmentService.createService(service);
    this.appointmentModalService
      .openCustomServiceModal(service, true)
      .then((data) => this.appointmentService.setCustomService(service, data));
  }

  private addServiceToCard(product: Product, employee: Employee) {
    let service = this.appointmentService.makeServiceToAddData(product, product?.price, employee);
    service = this.appointmentService.createService(service);
    this.appointmentService.addServiceToActiveAppointment(service);
  }

  private openProductVariantModal(product: Product, productCategoryUuid: string, eventTarget: any) {
    const listType = this.isServiceList ? PRODUCT_VARIANT_LIST_TYPE.SERVICES : PRODUCT_VARIANT_LIST_TYPE.PRODUCTS;
    this.collectionViewService.setProducVariantModalData({
      product,
      eventTarget,
      listType,
      productCategoryUuid,
    });
  }

  private async addProductToCart(variant: ProductVariant, product: Product, productCategoryUuid: string, productShortName: string) {
    const dataToAdd = this.invoicesService.makeInvoiceEntryToAddData(variant, product, productShortName, productCategoryUuid);

    dataToAdd.image = this.cartService.getEntryImage(product, variant);
    dataToAdd.assignedId = product?.assignedId || null;

    // saveRequired=false: the quantity is applied to the cart immediately (UI updates
    // per tap), but the expensive DB write + sync is NOT done on every tap. The persist
    // is coalesced via CartService.requestSave() so a burst of taps yields one save.
    // Awaited so the tracked handleProductClickProcess promise resolves only after the entry
    // (incl. linked deposit) is committed — see CartInteractionCoordinator ordering.
    await this.cartService.addEntry(dataToAdd, product.type, 1, false);
    this.cartService.requestSave();
    this.cdr.markForCheck();
  }

  private handleUpdateData(updateData, type: string) {
    const factory = (type: string, data: any) => {
      switch (type) {
        case UPDATES_TYPES.Product.type:
          return new Product(data);
        case UPDATES_TYPES.ProductCategory.type:
          return new ProductCategory(data);
      }
    };

    // Batch updates
    let hasChanges = false;

    for (let i = 0; i < updateData.length; i++) {
      const newItem: any = factory(type, updateData[i]['data']);
      const categories: string[] = newItem instanceof Product ? newItem.productCategoriesUuid?.replace(/,$/, '').split(',') : [newItem.parentCategoryUuid];
      const hasItem = this.productList.has(type, newItem.uuid);

      if (newItem instanceof Product) {
        newItem.visible = newItem.storeUuids.some((storeUuid: string): boolean => storeUuid === this.activeStore.uuid) && newItem.visible;
      }

      if (hasItem) {
        this.productList.update(type, newItem, categories);
        this.collectionViewService.setNewProductItemEvent(newItem);
        hasChanges = true;
      } else {
        this.productList.add(type, [newItem], categories);
        hasChanges = true;
      }
    }
    // Update UI only if there were changes
    if (hasChanges) {
      this.scheduleUpdateProductItemList();
    }
  }

  private updateCellParams() {
    const padding = this.platformService.isMobile ? 0 : 370;
    const inventoryAreaWidth = window.innerWidth - padding;
    if (inventoryAreaWidth < 0) {
      return;
    }
    const minCellWidth = this.platformService.isMobile ? (
       this.numbersOfProductsInRow === '4' ? (window.innerWidth < 400 ? 80 : 100) : 105
    ) : 115;
    const cellsQuantity = this.collectionViewService.calculateCellsQuantityInRow(inventoryAreaWidth, minCellWidth);
    const itemWidth = inventoryAreaWidth / cellsQuantity;
    this.cellWidth = Math.round((itemWidth / inventoryAreaWidth) * 100);
    if (cellsQuantity !== this.cellsQuantity) {
      this.setQuantity(cellsQuantity);
      this.scheduleUpdateProductItemList();
      return;
    }
    this.cdr.markForCheck();
  }

  private addRippleEffect(eventTarget: HTMLElement | undefined) {
    const rippleContainer = eventTarget?.querySelector('.ripple-container');
    if (rippleContainer) {
      this.renderer.addClass(rippleContainer, 'animate');
      setTimeout(() => this.renderer.removeClass(rippleContainer, 'animate'), 100);
    }
  }

  private notifyAboutProductDublicates(productList: Product[], categoryUuid: string = 'root') {
    const dublicates = productList.filter((product, index, array) => array.indexOf(product) !== index);
    if (dublicates.length) {
      dublicates.forEach((product) => this.logger.debug('Dublicate product, category', { productUuid: product.uuid, categoryUuid }));
    }
  }
}
